import { Frequency } from "@/types/date"
import { UnitSystem } from "@/types/geo"
import { TripsView } from "@/types/trips"
import { MeasurementSystem } from "@/types/user"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { selectOrganization } from "@/lib/storage/app-slice"
import type { RootState } from "./redux-store"

type StoredMeasurementSystem = MeasurementSystem | "imperial"

export const ChatBotVersions = {
  // ALERT: WHATEVER YOU ADD HERE, make sure it has at least 64k of context!
  mindtrip3_gpt4_1_64k: "Mindtrip3 GPT-4.1",
  mindtrip3_gpt5_2_64k: "Mindtrip3 GPT-5.2",
  mindtrip3_gpt5_3_64k: "Mindtrip3 GPT-5.3",
  mindtrip3_gpt5_4_64k: "Mindtrip3 GPT-5.4",
  default: "Default",
}
export type ChatBotVersionKey = keyof typeof ChatBotVersions
export const ChatBotVersionKeys = Object.keys(
  ChatBotVersions
) as ChatBotVersionKey[]

export type DashboardNavSectionId = "settings" | "create" | "internal" | "user"

export interface UiSettingsSliceState {
  chatBotVersion: ChatBotVersionKey
  isSiteNavCollapsed: boolean
  /**
   * @deprecated
   */
  unitSystem?: UnitSystem
  /**
   * The user's explicit unit-system choice.
   */
  unitSystemPreference?: StoredMeasurementSystem
  showDistance: boolean
  showCancelledBookings: boolean
  showMapPrices: boolean
  showMapAvatars: boolean
  showAppHomeMap: boolean
  tripsView: TripsView
  // following are DMO dashboard settings
  lastActiveOrgid?: string
  selectedDateLabel?: string
  selectedDateRange?: [string, string] // "YYYY-MM-DD" [from, to]
  selectedFrequency?: Frequency
  collapsedDashboardNavSections?: DashboardNavSectionId[]
}

export const uiSettingsSliceInitialState: UiSettingsSliceState = {
  chatBotVersion: "default",
  isSiteNavCollapsed: false,
  unitSystemPreference: undefined,
  showDistance: true,
  showCancelledBookings: false,
  showMapPrices: true,
  showMapAvatars: true,
  showAppHomeMap: false,
  tripsView: "all",
}

// Map legacy persisted tripsView keys (upcoming/recent) to all/activity.
function normalizeTripsView(view: unknown): TripsView {
  if (view === "all" || view === "activity") return view
  if (view === "upcoming") return "all"
  if (view === "recent") return "activity"
  return "all"
}

function migrateUnitSystemPreference(
  payload: UiSettingsSliceState
): MeasurementSystem | undefined {
  if (payload.unitSystemPreference === "imperial") return "us"
  if (payload.unitSystemPreference) return payload.unitSystemPreference
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  if (payload.unitSystem === "metric") return "metric"
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  if (payload.unitSystem === "imperial") return "us"
  return undefined
}

export const selectUiSettings = (state: RootState) => state.uiSettingsSlice

export const selectChatBotVersion = createSelector(
  [selectUiSettings],
  (state) => state.chatBotVersion
)

export const selectIsSiteNavCollapsed = createSelector(
  [selectUiSettings],
  (state) => state.isSiteNavCollapsed
)

export const selectUnitSystemPreference = createSelector(
  [selectUiSettings],
  (state): MeasurementSystem | undefined => {
    if (state.unitSystemPreference === "imperial") return "us"
    return state.unitSystemPreference
  }
)

export const selectShowDistance = createSelector(
  [selectUiSettings],
  (state) => state.showDistance
)

export const selectShowCancelledBookings = createSelector(
  [selectUiSettings],
  (state) => state.showCancelledBookings
)

export const selectShowMapPrices = createSelector(
  [selectUiSettings],
  (state) => state.showMapPrices
)

export const selectShowMapAvatars = createSelector(
  [selectUiSettings],
  (state) => state.showMapAvatars
)

export const selectShowAppHomeMap = createSelector(
  [selectUiSettings],
  (state) => state.showAppHomeMap ?? true
)

export const selectTripsView = createSelector([selectUiSettings], (state) =>
  normalizeTripsView(state.tripsView)
)

export const selectHideCreateTrip = createSelector(
  [selectOrganization],
  (org) => org?.properties.hide_create_trip || org?.business_type === "hotel"
)

// orgs that hide trips also hide trip-hub tabs beyond itinerary/chats/receipts
export const selectLimitTripFeatures = createSelector(
  [selectOrganization],
  (org) => !!org?.properties.hide_create_trip
)

// only the collapsed section ids are stored in cookie
export const selectCollapsedDashboardNavSections = createSelector(
  [selectUiSettings],
  (state): DashboardNavSectionId[] => state.collapsedDashboardNavSections ?? []
)

export const selectSelectedDateLabel = createSelector(
  [selectUiSettings],
  (state) => state.selectedDateLabel
)

export const selectSelectedDateRange = createSelector(
  [selectUiSettings],
  (state) => state.selectedDateRange
)

export const selectSelectedFrequency = createSelector(
  [selectUiSettings],
  (state) => state.selectedFrequency
)

const uiSettingsSlice = createSlice({
  name: "uiSettings",
  initialState: uiSettingsSliceInitialState,
  reducers: {
    resetUISettings: (state) => {
      Object.assign(state, uiSettingsSliceInitialState)
    },
    setChatBotVersion: (state, action: PayloadAction<ChatBotVersionKey>) => {
      state.chatBotVersion = action.payload
    },
    setSiteNavCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isSiteNavCollapsed = action.payload
    },
    setUnitSystemPreference: (
      state,
      action: PayloadAction<MeasurementSystem>
    ) => {
      state.unitSystemPreference = action.payload
    },
    toggleShowDistance: (state) => {
      state.showDistance = !state.showDistance
    },
    toggleShowCancelledBookings: (state) => {
      state.showCancelledBookings = !state.showCancelledBookings
    },
    toggleMapPrices: (state) => {
      state.showMapPrices = !state.showMapPrices
    },
    toggleMapAvatars: (state) => {
      state.showMapAvatars = !state.showMapAvatars
    },
    setShowAppHomeMap: (state, action: PayloadAction<boolean>) => {
      state.showAppHomeMap = action.payload
    },
    setTripsView: (state, action: PayloadAction<TripsView>) => {
      state.tripsView = action.payload
    },
    setDashboardNavSectionCollapsed: (
      state,
      action: PayloadAction<{
        section: DashboardNavSectionId
        collapsed: boolean
      }>
    ) => {
      const { section, collapsed } = action.payload
      const sections = state.collapsedDashboardNavSections ?? []
      state.collapsedDashboardNavSections = collapsed
        ? [...sections.filter((it) => it !== section), section]
        : sections.filter((it) => it !== section)
    },
    setLastActiveOrgid: (state, action: PayloadAction<string>) => {
      state.lastActiveOrgid = action.payload
    },
    setSelectedDateLabel: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.selectedDateLabel = action.payload
    },
    setSelectedDateRange: (
      state,
      action: PayloadAction<[string, string] | undefined>
    ) => {
      state.selectedDateRange = action.payload
    },
    setSelectedFrequency: (
      state,
      action: PayloadAction<Frequency | undefined>
    ) => {
      state.selectedFrequency = action.payload
    },
    setUISettings: (state, action: PayloadAction<UiSettingsSliceState>) => {
      Object.assign(state, {
        ...action.payload,
        tripsView: normalizeTripsView(action.payload.tripsView),
        unitSystemPreference: migrateUnitSystemPreference(action.payload),
      })
    },
  },
})

export const {
  setUISettings,
  resetUISettings,
  setChatBotVersion,
  setSiteNavCollapsed,
  setUnitSystemPreference,
  toggleShowDistance,
  toggleShowCancelledBookings,
  toggleMapPrices,
  toggleMapAvatars,
  setShowAppHomeMap,
  setTripsView,
  setDashboardNavSectionCollapsed,
  setLastActiveOrgid,
  setSelectedDateLabel,
  setSelectedDateRange,
  setSelectedFrequency,
} = uiSettingsSlice.actions

export default uiSettingsSlice.reducer
